// src/pages/ProjectListPage.jsx
//
// One row per project (Option B, per Jerome's call), with a compact
// StatusBlock per stage so Permit/Fieldwork/Site data/Final report/Digital
// data/Artifacts can be claimed right from the list -- closer to how
// AFSC's task list works, instead of forcing a click into the detail page
// just to claim something.
//
// Each stage's full list is fetched once (GET /api/tracker/permits/, etc.
// -- no project filter, so it returns every row) and joined to projects by
// project id client-side. That's 7 requests total no matter how many
// projects there are, instead of 6 requests per row.
//
// Only the main status trio is shown here, not the revision_* trio on
// Site_Data/Final_Report -- revisions are a secondary workflow, so they
// stay on the detail page (click the project number) along with the
// editable due_date/permit_no/etc. fields.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../api/client";
import { createAndClaim } from "../api/stageActions";
import Navbar from "../components/Navbar";
import StatusBlock from "../components/StatusBlock";
import { STATUS_CHOICES, FIELDWORK_STATUS_CHOICES } from "../constants";

const STAGES = [
  {
    key: "permit",
    title: "Permit",
    basePath: "/tracker/permits",
    statusField: "application_status",
    statusChoices: STATUS_CHOICES,
  },
  {
    key: "fieldwork",
    title: "Fieldwork",
    basePath: "/tracker/fieldwork",
    statusField: "status",
    statusChoices: FIELDWORK_STATUS_CHOICES,
  },
  {
    key: "site_data",
    title: "Site data",
    basePath: "/tracker/site-data",
    statusField: "status",
    statusChoices: STATUS_CHOICES,
  },
  {
    key: "final_report",
    title: "Final report",
    basePath: "/tracker/final-reports",
    statusField: "status",
    statusChoices: STATUS_CHOICES,
  },
  {
    key: "digital_data",
    title: "Digital data",
    basePath: "/tracker/digital-data",
    statusField: "status",
    statusChoices: STATUS_CHOICES,
  },
  {
    key: "artifact_submission",
    title: "Artifacts",
    basePath: "/tracker/artifact-submissions",
    statusField: "status",
    statusChoices: STATUS_CHOICES,
  },
];

// Missing rows are expected for Permit (genuinely 0-or-1) and, until the
// backfill_stage_rows management command has been run, possibly for older
// projects on the other five stages too. Either way, "claim" here creates
// the row and claims it in one step (see api/stageActions.createAndClaim)
// instead of dead-ending on a plain "--".
function MissingStageCell({ stage, projectId, onClaimed }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleClaim = async () => {
    setBusy(true);
    setError("");
    try {
      const row = await createAndClaim(stage.basePath, projectId);
      onClaimed(row);
    } catch (err) {
      setError(err.message || "Couldn't claim.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="status-block-compact">
      <div className="status-block-compact-row">
        <span className="status-badge status-none">--</span>
        <button type="button" disabled={busy} onClick={handleClaim}>
          Claim
        </button>
      </div>
      {error && <p className="form-error form-error-compact">{error}</p>}
    </div>
  );
}

export default function ProjectListPage() {
  // Read fresh on mount, not at module load -- see StageCard.jsx for why
  // (a stale read here otherwise hides Unclaim/status controls for
  // everyone right after login until they hard-refresh).
  const currentUsername = localStorage.getItem("username");
  const isAdmin = localStorage.getItem("role") === "admin";

  const [projects, setProjects] = useState([]);
  // stageData.permit[projectId] = that permit's row, etc.
  const [stageData, setStageData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    Promise.all([
      apiFetch("/tracker/projects/"),
      ...STAGES.map((stage) => apiFetch(`${stage.basePath}/`)),
    ])
      .then(([projectList, ...stageLists]) => {
        if (cancelled) return;
        setProjects(projectList);
        const byStage = {};
        STAGES.forEach((stage, i) => {
          const byProjectId = {};
          stageLists[i].forEach((row) => {
            byProjectId[row.project] = row;
          });
          byStage[stage.key] = byProjectId;
        });
        setStageData(byStage);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load projects.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const updateStageRow = (stageKey, projectId, row) => {
    setStageData((prev) => ({
      ...prev,
      [stageKey]: { ...prev[stageKey], [projectId]: row },
    }));
  };

  const postAction = async (stage, projectId, actionPath, body) => {
    const row = await apiFetch(`${stage.basePath}/${projectId}/${actionPath}/`, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
    updateStageRow(stage.key, projectId, row);
  };

  return (
    <>
      <Navbar />
      <main className="page page-wide">
        <div className="page-header">
          <h1>Projects</h1>
          <Link to="/projects/new" className="btn-primary">
            New project
          </Link>
        </div>

        {loading && <p>Loading...</p>}
        {error && <p className="form-error">{error}</p>}

        {!loading && !error && (
          <div className="table-wrap table-wrap-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Project #</th>
                  <th>Name</th>
                  <th>Proponent</th>
                  <th>Type</th>
                  <th>Award date</th>
                  {STAGES.map((stage) => (
                    <th key={stage.key}>{stage.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projects.length === 0 && (
                  <tr>
                    <td colSpan={5 + STAGES.length} className="empty-row">
                      No projects yet.
                    </td>
                  </tr>
                )}
                {projects.map((project) => (
                  <tr key={project.id}>
                    <td>
                      <Link to={`/projects/${project.id}`}>{project.project_no}</Link>
                    </td>
                    <td>{project.project_name || "--"}</td>
                    <td>{project.proponent_name || "--"}</td>
                    <td>{project.project_type_code || "--"}</td>
                    <td>{project.award_date || "--"}</td>
                    {STAGES.map((stage) => {
                      const row = stageData[stage.key]?.[project.id];
                      if (!row) {
                        return (
                          <td key={stage.key}>
                            <MissingStageCell
                              stage={stage}
                              projectId={project.id}
                              onClaimed={(newRow) =>
                                updateStageRow(stage.key, project.id, newRow)
                              }
                            />
                          </td>
                        );
                      }
                      return (
                        <td key={stage.key}>
                          <StatusBlock
                            compact
                            status={row[stage.statusField]}
                            assignedToUsername={row.assigned_to_username}
                            statusChoices={stage.statusChoices}
                            currentUsername={currentUsername}
                            isAdmin={isAdmin}
                            onClaim={() => postAction(stage, project.id, "claim")}
                            onUnclaim={() => postAction(stage, project.id, "unclaim")}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </>
  );
}
